export interface RefreshResponse {
  token: string;
  refreshToken: string;
  accessTokenExpiry: number;
  scope: string;
}

/** Default when the API response carries no usable error message (e.g. a non-JSON
 * 502/503 gateway page). Treated as "no message" so the diagnostic status line is
 * surfaced instead of this opaque string. */
export const UNKNOWN_API_ERROR = "Unknown error occurred";

/**
 * Preferred user-facing message for a failed request: the API's own message
 * (e.g. "End the tenant's meter assignments before archiving") over the
 * generic "<status> error when performing <method> on <url>".
 *
 * Falls through to the diagnostic message when the API gave no real message
 * (the UNKNOWN_API_ERROR sentinel) — otherwise a 502/503/429/non-JSON gateway
 * error would surface as "Unknown error occurred" with the status code lost.
 */
export function getApiErrorMessage(e: unknown): string {
  if (e instanceof APIError && e.apiErrorMessage && e.apiErrorMessage !== UNKNOWN_API_ERROR) {
    return e.apiErrorMessage;
  }
  return e instanceof Error ? e.message : String(e);
}

export class APIError extends Error {
  statusCode: number;
  url: string;
  apiErrorMessage: string;

  constructor(message: string, statusCode: number, url: string, apiErrorMessage: string) {
    super(message);

    this.statusCode = statusCode;
    this.url = url;
    this.apiErrorMessage = apiErrorMessage;
  }

  /** Build an APIError from a fetch Response */
  static async fromResponse(response: Response, request: RequestInit) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let body: any = {};
    try {
      body = await response.json();
    } catch {
      // ignore, non-JSON response
    }

    const apiErrorMessage =
      body?.errors?.[0]?.message ?? body?.message ?? body?.error_description ?? "Unknown error occurred";

    const message = `${response.status} error when performing ${request.method} on ${response.url}`;

    return new APIError(message, response.status, response.url, apiErrorMessage);
  }

  get isServerError() {
    return this.statusCode == 500;
  }
}

export const baseURL = import.meta.env.VITE_API_HOST;

export const appClientDetails = {
  client_id: import.meta.env.VITE_CLIENT_ID,
  client_secret: import.meta.env.VITE_CLIENT_SECRET,
};
